import { anchorAttributes, mapHtmlAnchors, someHtmlAnchor } from './html-anchors'
import { type IndexingPolicy, linkRel, requiresDocumentUrl } from './indexing-policy'

const decodeAttribute = (value: string) =>
  value.replace(/&(?:amp|quot|apos|lt|gt|#\d+|#x[\da-f]+);/gi, (entity) => {
    const names: Record<string, string> = { '&amp;': '&', '&quot;': '"', '&apos;': "'", '&lt;': '<', '&gt;': '>' }
    if (names[entity.toLowerCase()]) {
      return names[entity.toLowerCase()]
    }
    const numeric = entity.slice(2, -1)
    const code = numeric[0].toLowerCase() === 'x' ? parseInt(numeric.slice(1), 16) : Number(numeric)
    return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : entity
  })

export const hasRelativeHtmlLinks = (html: string): boolean =>
  someHtmlAnchor(html, (tag) => {
    const href = anchorAttributes(tag).find((attribute) => attribute.name === 'href')
    return Boolean(href && requiresDocumentUrl(decodeAttribute(href.value)))
  })

// Process real anchor tags only, leaving comments, scripts, styles and text
// areas intact. Handles quoted > characters and preserves authored rel tokens.
export const htmlLinkRel = (html: string, policy: IndexingPolicy | null, documentUrl?: string): string =>
  mapHtmlAnchors(html, (tag: string) => {
    const attributes = anchorAttributes(tag)
    const href = attributes.find((attribute) => attribute.name === 'href')
    const rel = attributes.find((attribute) => attribute.name === 'rel')
    const target = attributes.find((attribute) => attribute.name === 'target')
    const relValue = rel ? decodeAttribute(rel.value) : undefined
    const nextRel = linkRel(
      href ? decodeAttribute(href.value) : undefined,
      relValue,
      policy,
      documentUrl,
      target ? decodeAttribute(target.value) : undefined
    )
    if (nextRel === relValue) {
      return tag
    }
    const escaped = nextRel!.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
    const start = rel?.start ?? attributes.at(-1)?.end ?? 2
    const end = rel?.end ?? start
    return tag.slice(0, start) + ` rel="${escaped}"` + tag.slice(end)
  })
